import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "@phosphor-icons/react";

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      nav("/app/organization");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="flex items-center justify-center px-6 py-10 order-2 md:order-1">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-2">ISO 14064 · GHG</div>
            <h1 className="font-display text-3xl font-semibold">{t("createAccount")}</h1>
            <p className="text-muted-foreground text-sm mt-1">{t("tagline")}</p>
          </div>
          {err && (
            <div className="text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2" data-testid="register-error">
              {err}
            </div>
          )}
          <div className="space-y-2">
            <Label>{t("name")}</Label>
            <Input required value={form.name} onChange={upd("name")} data-testid="register-name" />
          </div>
          <div className="space-y-2">
            <Label>{t("email")}</Label>
            <Input type="email" required value={form.email} onChange={upd("email")} data-testid="register-email" />
          </div>
          <div className="space-y-2">
            <Label>{t("password")}</Label>
            <Input type="password" required value={form.password} onChange={upd("password")} data-testid="register-password" />
          </div>
          <div className="space-y-2">
            <Label>{t("organization")}</Label>
            <Input value={form.organization} onChange={upd("organization")} data-testid="register-org" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
            {loading ? "…" : t("signUp")}
          </Button>
          <div className="text-sm text-muted-foreground">
            {t("haveAccount")}{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-login">
              {t("signIn")}
            </Link>
          </div>
        </form>
      </div>
      <div
        className="hidden md:block relative overflow-hidden order-1 md:order-2"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1778825431036-82541fa3ebc7?crop=entropy&cs=srgb&fm=jpg&h=1000&w=800)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-primary-foreground">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-md bg-primary-foreground/20 border border-primary-foreground/30 flex items-center justify-center backdrop-blur-md">
              <Leaf size={22} weight="fill" />
            </div>
            <div className="font-display text-lg font-semibold">Enasuspro® Carbon Ledger</div>
            <div className="text-[10px] uppercase tracking-widest opacity-70 ml-auto">Sustainability Coaching</div>
          </div>
          <div>
            <h2 className="font-display text-4xl font-medium tracking-tight leading-tight">
              {t("heroHeadline")}
            </h2>
            <p className="mt-4 text-base opacity-90 max-w-md">{t("heroSub")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
